import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PlusCircle, ExternalLink, Trash2, Plus } from 'lucide-react';
import { getVideoUrl } from '@/lib/youtubeApi';
import type { VideoItem, Collection } from '@/lib/supabase';
import { createCollection, addVideoToCollection } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCollections } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';

interface VideoCardProps {
  video: VideoItem;
  collections?: Collection[];
  inCollection?: boolean;
  onAdd?: (collectionId: string) => void;
  onRemove?: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ 
  video, 
  collections: propCollections,
  inCollection = false,
  onAdd,
  onRemove
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');

  const { data: fetchedCollections = [] } = useQuery({
    queryKey: ['collections'],
    queryFn: getCollections,
    enabled: !propCollections && !!user && isDialogOpen
  });

  const collections = propCollections || fetchedCollections;

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };

  const handleCollectionSelect = async (collectionId: string) => {
    try {
      await addVideoToCollection(collectionId, video);
      
      // Only call onAdd if it exists
      if (onAdd) {
        onAdd(collectionId);
      }
      
      setIsDialogOpen(false);
      
      toast({
        title: "Success",
        description: "Video added to collection"
      });
      
      queryClient.invalidateQueries(['collections']);
    } catch (error) {
      console.error('Error adding video:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add video to collection",
        variant: "destructive"
      });
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim() || !user) return;

    setIsCreating(true);
    try {
      const newCollection = await createCollection({
        name: newCollectionName.trim(),
        description: newCollectionDescription.trim(),
        user_id: user.id,
        is_public: false,
        tags: []
      });

      await addVideoToCollection(newCollection.id!, video);

      // Only call onAdd if it exists and we have a collection ID
      if (onAdd && newCollection.id) {
        onAdd(newCollection.id);
      }

      setNewCollectionName('');
      setNewCollectionDescription('');
      setIsCreating(false);
      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: "Collection created and video added"
      });

      queryClient.invalidateQueries(['collections']);
    } catch (error) {
      console.error('Error creating collection:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create collection",
        variant: "destructive"
      });
      setIsCreating(false);
    }
  };

  const handleWatchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(getVideoUrl(video.videoId), '_blank');
  };

  return (
    <>
      <div className="video-card bg-card rounded-lg overflow-hidden border border-border shadow-md">
        <div className="relative aspect-video">
          <img 
            src={video.thumbnail} 
            alt={video.title} 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="p-4">
          <h3 className="font-medium text-base line-clamp-2 mb-3">{video.title}</h3>
          <div className="flex justify-between items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleWatchClick}
              className="text-muted-foreground hover:text-accent flex items-center gap-1"
            >
              <ExternalLink size={16} />
              Watch
            </Button>
            {inCollection ? (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onRemove}
                className="text-destructive hover:text-destructive-foreground hover:bg-destructive/20"
              >
                <Trash2 size={16} />
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleAddClick}
                className="text-accent hover:text-accent-foreground hover:bg-accent/20"
              >
                <PlusCircle size={16} className="mr-1" />
                Add
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Collection</DialogTitle>
          </DialogHeader>

          {collections.length > 0 && (
            <ScrollArea className="h-40 pr-4 mb-2">
              <div className="space-y-1">
                {collections.map(collection => (
                  <Card 
                    key={collection.id}
                    className="cursor-pointer hover:bg-accent/10 transition-colors"
                    onClick={() => handleCollectionSelect(collection.id!)}
                  >
                    <CardHeader className="p-2">
                      <h3 className="font-medium">{collection.name}</h3>
                      {collection.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {collection.description}
                        </p>
                      )}
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}

          {collections.length > 0 && (
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  or create new
                </span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {collections.length === 0 && (
              <label className="text-sm font-medium">Create New Collection</label>
            )}
            <Input
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              placeholder="Enter collection name"
            />
            <Textarea
              value={newCollectionDescription}
              onChange={(e) => setNewCollectionDescription(e.target.value)}
              placeholder="Enter collection description (optional)"
              rows={2}
              className="min-h-0"
            />
            <Button 
              className="w-full"
              onClick={handleCreateCollection}
              disabled={isCreating || !newCollectionName.trim()}
            >
              <Plus size={16} className="mr-2" />
              Create & Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoCard;